import React, { useCallback } from 'react';
import { FormikProps } from 'formik';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { FieldError } from '../_core/Formik/field-error.component';
import { SelectableChip } from '../_core/_ui/selectable-chip.component';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { pluralTerm } from '../../lib/helpers/term.helper';
import { SportTerminology } from '../../lib/types';
import { messages } from './format-template.messages';
import { IFormatTemplateMetadataFields } from './format-template-metadata.types';

interface IFormatTemplateMetadataFieldsProps {
  form: FormikProps<IFormatTemplateMetadataFields>;
  bucketOptions: string[];
}

// Name + recommendation inputs (age groups / min-max players / team outcomes) shared by the create
// and edit template modals, so both surfaces edit the same metadata identically.
export const FormatTemplateMetadataFields: React.FC<IFormatTemplateMetadataFieldsProps> = ({ form, bucketOptions }) => {
  const { values, errors, touched, isSubmitting, handleChange, handleBlur, setFieldValue } = form;
  const termLookup = useCompanySportTerminology();
  const skaterWord = pluralTerm(termLookup[SportTerminology.SKATER]).toLowerCase();

  const toggleBucket = useCallback((bucket: string) => {
    const next = values.ageGroupBuckets.includes(bucket)
      ? values.ageGroupBuckets.filter(b => b !== bucket)
      : [...values.ageGroupBuckets, bucket];
    setFieldValue('ageGroupBuckets', next);
  }, [values.ageGroupBuckets, setFieldValue]);

  return (
    <>
      <TextField
        name="name"
        label={<IntlMsg {...messages.templateNameLabel} />}
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.name && !!errors.name}
        helperText={touched.name && <FieldError error={errors.name} catalog={messages} />}
        fullWidth
        variant="outlined"
        size="small"
        autoFocus
        disabled={isSubmitting}
      />

      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom><IntlMsg {...messages.recommendationsTitle} /></Typography>

        <Box mt={1}>
          <InputLabel shrink><IntlMsg {...messages.ageGroupsLabel} /></InputLabel>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {bucketOptions.map(bucket => (
              <SelectableChip
                key={bucket}
                label={bucket}
                selected={values.ageGroupBuckets.includes(bucket)}
                onClick={() => toggleBucket(bucket)}
                disabled={isSubmitting}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" mt={2} style={{ gap: 12 }}>
          <TextField
            name="minPlayers"
            label={<IntlMsg {...messages.minPlayersLabel} values={{ skaterWord }} />}
            type="number"
            value={values.minPlayers}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.minPlayers && !!errors.minPlayers}
            helperText={touched.minPlayers && <FieldError error={errors.minPlayers} catalog={messages} />}
            fullWidth
            variant="outlined"
            size="small"
            disabled={isSubmitting}
          />
          <TextField
            name="maxPlayers"
            label={<IntlMsg {...messages.maxPlayersLabel} values={{ skaterWord }} />}
            type="number"
            value={values.maxPlayers}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.maxPlayers && !!errors.maxPlayers}
            helperText={touched.maxPlayers && <FieldError error={errors.maxPlayers} catalog={messages} />}
            fullWidth
            variant="outlined"
            size="small"
            disabled={isSubmitting}
          />
        </Box>

        <Box mt={2}>
          <TextField
            name="teamOutcomes"
            label={<IntlMsg {...messages.teamOutcomesLabel} />}
            value={values.teamOutcomes}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            variant="outlined"
            size="small"
            disabled={isSubmitting}
          />
          <FormHelperText><IntlMsg {...messages.teamOutcomesHelp} /></FormHelperText>
        </Box>
      </Box>
    </>
  );
};
